import {
  withValidation,
  composeSDKFactories,
  createElementPropsSDKFactory,
  clickPropsSDKFactory,
  reportError,
  createCompSchemaValidator,
  getLink,
  setLink,
  createAccessibilityPropSDKFactory,
} from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import type {
  ICollapsibleTextSDKFactory,
  ICollapsibleTextMapperProps,
  ICollapsibleTextSDK,
  ExpandOnCurrentPage,
  LinkToContent,
  ICollapsibleTextCompProps,
} from '../CollapsibleText.types';
import { ReadMoreActionType } from '../CollapsibleText.types';
import { actionTypeError } from '../constants';
import {
  expandOnCurrentPageSchema,
  linkToContentSchema,
  validationSchema,
} from './CollapsibleText.validations';

const CollapsibleTextSDKFactory: ICollapsibleTextSDKFactory = ({
  setProps,
  setStyles,
  props,
  platformUtils: { linkUtils },
  metaData,
}) => {
  const schemaValidator = createCompSchemaValidator(metaData.role);
  const isLinkToContent = (obj: unknown): obj is LinkToContent =>
    schemaValidator(obj, linkToContentSchema, 'readMoreActionData');
  const isExpandOnCurrentPage = (obj: unknown): obj is ExpandOnCurrentPage =>
    schemaValidator(obj, expandOnCurrentPageSchema, 'readMoreActionData');
  const getLinkReadMoreAction = (
    action: LinkToContent,
  ): Partial<ICollapsibleTextCompProps> => ({
    showExpandControl: action.readMoreButtonVisible ?? props.showExpandControl,
    linkText: action.readMoreButtonText
      ? action.readMoreButtonText
      : props.linkText,
    expandMode: 'link',
  });
  const getExpandReadMoreAction = (
    action: ExpandOnCurrentPage,
  ): Partial<ICollapsibleTextCompProps> => ({
    readMoreText: action.readMoreButtonText
      ? action.readMoreButtonText
      : props.readMoreText,
    readLessText: action.readLessButtonText ?? props.readLessText,
    isExpanded:
      action.collapsed !== undefined ? !action.collapsed : props.isExpanded,
    expandMode: 'button',
  });

  return {
    get text(): string {
      return props.text;
    },
    set text(value: string) {
      setProps({ text: value });
    },
    get ellipsisEnabled(): boolean {
      return props.ellipsis;
    },
    get maxLines(): number {
      return props.maxLines;
    },
    set maxLines(maxLines: number) {
      setStyles({
        '--max-lines': `${maxLines} !important`,
      });
      setProps({ maxLines });
    },
    get readMoreActionType(): ReadMoreActionType {
      return props.expandMode === 'link'
        ? ReadMoreActionType.LinkToContent
        : ReadMoreActionType.ExpandOnCurrentPage;
    },
    set readMoreActionType(type: ReadMoreActionType) {
      setProps({
        expandMode:
          type === ReadMoreActionType.LinkToContent ? 'link' : 'button',
      });
    },
    get readMoreActionData(): LinkToContent | ExpandOnCurrentPage {
      return props.expandMode === 'link'
        ? ({
            link: getLink(props, linkUtils),
            target: props.link?.target,
            readMoreButtonVisible: props.showExpandControl,
            readMoreButtonText: props.readMoreText,
          } as LinkToContent)
        : ({
            readMoreButtonText: props.readMoreText,
            readLessButtonText: props.readLessText,
            collapsed: !props.isExpanded,
          } as ExpandOnCurrentPage);
    },
    set readMoreActionData(action: LinkToContent | ExpandOnCurrentPage) {
      if (isLinkToContent(action) && props.expandMode === 'link') {
        setLink(action.link, action.target, linkUtils, setProps);
        setProps(getLinkReadMoreAction(action));
      } else if (
        isExpandOnCurrentPage(action) &&
        props.expandMode === 'button'
      ) {
        setProps(getExpandReadMoreAction(action));
      } else {
        reportError(actionTypeError);
      }
    },
    expandText() {
      if (props.expandMode === 'button') {
        setProps({ isExpanded: true });
      } else {
        reportError(actionTypeError);
      }
    },
    collapseText() {
      if (props.expandMode === 'button') {
        setProps({ isExpanded: false });
      } else {
        reportError(actionTypeError);
      }
    },
    removeReadMoreButton() {
      if (props.expandMode === 'link') {
        setProps({ showExpandControl: false });
      } else {
        reportError(actionTypeError);
      }
    },
    addReadMoreButton(text?: string) {
      if (props.expandMode === 'link') {
        setProps({
          linkText: text ?? props.linkText,
          showExpandControl: true,
        });
      } else {
        reportError(actionTypeError);
      }
    },
    disableEllipsis() {
      setProps({ ellipsis: false });
    },
    enableEllipsis() {
      setProps({ ellipsis: true });
    },
  } as ICollapsibleTextSDK;
};
const CollapsibleTextSDK = withValidation(
  CollapsibleTextSDKFactory,
  validationSchema,
);

const accessibilitySDKFactory = createAccessibilityPropSDKFactory({
  enableLang: true,
});

export const sdk = composeSDKFactories<
  ICollapsibleTextMapperProps,
  ICollapsibleTextSDK
>([
  createElementPropsSDKFactory(),
  CollapsibleTextSDK,
  clickPropsSDKFactory,
  accessibilitySDKFactory,
]);

export default createComponentSDKModel(sdk);
